/**
 * SampleDataViewer Component
 *
 * Displays sample rows from a table in a responsive scrollable table
 * Highlights the selected watermark column
 */

import { TableSampleDataResponse } from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';

interface SampleDataViewerProps {
  data: TableSampleDataResponse;
  highlightColumn?: string | null;
}

export function SampleDataViewer({ data, highlightColumn }: SampleDataViewerProps) {
  // Format cell value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'NULL';
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    if (typeof value === 'object') {
      // Handle dates
      if (value instanceof Date) {
        return value.toISOString();
      }
      // Handle other objects
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Check if value is NULL
  const isNull = (value: any): boolean => {
    return value === null || value === undefined;
  };

  if (data.rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p>No sample data available for this table</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {data.totalRows} sample {data.totalRows === 1 ? 'row' : 'rows'} from{' '}
          <code className="font-mono">{data.tableName}</code>
        </p>
        {highlightColumn && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Highlighting: <code className="ml-1 font-mono">{highlightColumn}</code>
          </Badge>
        )}
      </div>

      <ScrollArea className="h-[400px] rounded-md border">
        <div className="min-w-max">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-12 text-center bg-muted">#</TableHead>
                {data.columns.map((column) => {
                  const isHighlighted = column === highlightColumn;
                  return (
                    <TableHead
                      key={column}
                      className={`${
                        isHighlighted
                          ? 'bg-blue-100 dark:bg-blue-900/30 font-bold'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <code className="text-xs">{column}</code>
                        {isHighlighted && (
                          <Badge variant="secondary" className="text-xs">
                            Selected
                          </Badge>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell className="text-center text-muted-foreground font-mono text-xs bg-muted/50">
                    {rowIndex + 1}
                  </TableCell>
                  {data.columns.map((column) => {
                    const value = row[column];
                    const isHighlighted = column === highlightColumn;
                    const isNullValue = isNull(value);

                    return (
                      <TableCell
                        key={`${rowIndex}-${column}`}
                        className={`max-w-xs truncate ${
                          isHighlighted
                            ? 'bg-blue-50 dark:bg-blue-900/20 font-medium'
                            : ''
                        } ${
                          isNullValue
                            ? 'text-muted-foreground italic'
                            : 'font-mono text-xs'
                        }`}
                        title={formatValue(value)}
                      >
                        {isNullValue ? (
                          <span className="text-muted-foreground">NULL</span>
                        ) : (
                          formatValue(value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>• Values are truncated for display. Hover over cells to see full content.</p>
        <p>• NULL values are shown in gray italic text.</p>
        <p>• Scroll horizontally to see all columns.</p>
      </div>
    </div>
  );
}
